import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    MapPin,
    Phone,
    Clock,
    Star,
    ArrowUpRight,
    Truck,
    BadgeCheck,
    Navigation,
} from "lucide-react";
import SEOHead from "../components/SEOHead";
import SchemaJsonLd from "../components/SchemaJsonLd";
import MapWidget from "../components/MapWidget";
import { getLocation } from "../lib/api";
import { localBusinessSchema, breadcrumbSchema, webPageSchema, slugify } from "../lib/seo";
import { useLiveReviews } from "../lib/useLiveReviews";

export default function LocationDetail({ brand }) {
    const { slug } = useParams();
    const [loc, setLoc] = useState(null);
    const [error, setError] = useState(null);
    const live = useLiveReviews(slug, 4.9, 0);

    useEffect(() => {
        let cancelled = false;
        getLocation(slug)
            .then((d) => !cancelled && setLoc(d))
            .catch(() => !cancelled && setError("not found"));
        return () => {
            cancelled = true;
        };
    }, [slug]);
    const isStale = loc && loc.slug !== slug;

    if (error) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-32 text-center">
                <SEOHead
                    title="Location not found"
                    description="This ZenZest dispensary location is not listed. Browse all NY OCM-licensed ZenZest stores."
                    canonical={`https://weeddispensarynearme.com/locations/${slug}`}
                />
                <h1 className="font-serif text-4xl font-light mb-6 text-zen-ink">
                    Location not found.
                </h1>
                <Link to="/locations" className="text-zen-olive underline">
                    Browse all locations
                </Link>
            </div>
        );
    }
    if (!loc || isStale) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-32 text-zen-ink-soft text-center">
                <SEOHead
                    title="Loading location…"
                    description="Loading the latest ZenZest dispensary location details."
                    canonical={`https://weeddispensarynearme.com/locations/${slug}`}
                />
                Loading…
            </div>
        );
    }

    const canonical = `https://weeddispensarynearme.com/locations/${loc.slug}`;
    // Live-resolved rating/count override the static fallback in /backend/data/locations.py
    // so SEO metas + on-page badge stay in sync with Google Places.
    const rating = live.loaded ? live.rating : loc.rating;
    const reviewCount = live.loaded ? live.count : loc.review_count;
    const locForSchema = { ...loc, rating, review_count: reviewCount };

    return (
        <>
            <SEOHead
                title={`${loc.short_name} Cannabis Dispensary | ${loc.street}`}
                description={`${loc.name} — NY OCM-licensed adult-use dispensary at ${loc.street}, ${loc.city}, ${loc.state} ${loc.zip}. ${Number(rating).toFixed(1)}★ from ${Number(reviewCount).toLocaleString()}+ Google reviews. Free same-day delivery.`}
                canonical={canonical}
                image={loc.image}
            />
            <SchemaJsonLd
                schema={[
                    webPageSchema({ url: canonical, name: loc.name }),
                    localBusinessSchema(locForSchema, brand),
                    breadcrumbSchema([
                        { name: "Home", url: "https://weeddispensarynearme.com/" },
                        {
                            name: "Locations",
                            url: "https://weeddispensarynearme.com/locations",
                        },
                        { name: loc.short_name, url: canonical },
                    ]),
                ]}
            />

            {/* HERO (dark) */}
            <section
                className="relative overflow-hidden bg-zen-elevated"
                style={{
                    backgroundImage: `linear-gradient(180deg, rgba(40,48,31,0.55), rgba(40,48,31,0.92)), url('${loc.image}')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 pt-24 pb-20 sm:pt-32 sm:pb-32">
                    <nav className="text-[11px] uppercase tracking-[0.25em] text-zen-cream-muted mb-8">
                        <Link to="/" className="hover:text-white">
                            Home
                        </Link>{" "}
                        ·{" "}
                        <Link to="/locations" className="hover:text-white">
                            Locations
                        </Link>{" "}
                        ·{" "}
                        <span className="text-zen-olive-light">
                            {loc.short_name}
                        </span>
                    </nav>
                    <p className="text-[11px] uppercase tracking-[0.35em] text-zen-olive-light mb-6 flex items-center gap-3">
                        <span className="w-8 h-px bg-zen-olive-light" />
                        NY OCM License · {loc.license}
                    </p>
                    <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl font-light text-white leading-[0.95] tracking-tight max-w-4xl">
                        {loc.name}
                    </h1>
                    <p className="mt-8 max-w-2xl text-lg text-white/85 leading-relaxed">
                        {loc.pitch}
                    </p>
                    <div className="mt-12 flex flex-wrap gap-3">
                        <a
                            href={loc.menu_url || loc.store_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`store-shop-${loc.slug}`}
                            className="inline-flex items-center gap-2 bg-zen-olive hover:bg-zen-olive-dark text-zen-bg px-8 py-4 font-medium uppercase tracking-[0.25em] text-[11px] transition-colors"
                        >
                            Shop this store <ArrowUpRight size={14} />
                        </a>
                        <Link
                            to={`/menu/${loc.slug}`}
                            data-testid={`store-livemenu-${loc.slug}`}
                            className="inline-flex items-center gap-2 bg-zen-bg hover:bg-white text-zen-ink px-8 py-4 font-medium uppercase tracking-[0.25em] text-[11px] transition-colors border border-zen-bg"
                        >
                            Browse live menu →
                        </Link>
                        <a
                            href={`https://maps.google.com/?q=${encodeURIComponent(loc.street + ", " + loc.city + ", " + loc.state + " " + loc.zip)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`store-directions-${loc.slug}`}
                            className="inline-flex items-center gap-2 border border-white/40 hover:border-white hover:bg-white hover:text-zen-ink text-white px-8 py-4 font-medium uppercase tracking-[0.25em] text-[11px] transition-all"
                        >
                            <Navigation size={14} /> Get directions
                        </a>
                        <a
                            href={`tel:${loc.phone_tel || loc.phone}`}
                            data-testid={`store-call-${loc.slug}`}
                            className="inline-flex items-center gap-2 border border-white/40 hover:border-white hover:bg-white hover:text-zen-ink text-white px-8 py-4 font-medium uppercase tracking-[0.25em] text-[11px] transition-all"
                        >
                            <Phone size={14} /> {loc.phone}
                        </a>
                        {loc.slug === "queens-long-island" && (
                            <Link
                                to="/visit/queens"
                                data-testid="store-visit-queens-flagship"
                                className="inline-flex items-center gap-2 bg-white text-zen-ink px-8 py-4 font-medium uppercase tracking-[0.25em] text-[11px] hover:bg-zen-cream transition-all"
                            >
                                Visit the flagship
                            </Link>
                        )}
                    </div>
                </div>
            </section>

            {/* DETAILS (light) */}
            <section className="bg-zen-bg max-w-[1400px] mx-auto px-6 sm:px-10 py-14">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-7">
                        <p className="text-[11px] uppercase tracking-[0.35em] text-zen-olive mb-4">
                            At a glance
                        </p>
                        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-zen-ink leading-[1.05] mb-6">
                            What makes this store {loc.short_name}&rsquo;s
                            favorite.
                        </h2>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5 mb-8">
                            {loc.highlights.map((h, i) => (
                                <li
                                    key={i}
                                    className="flex items-start gap-3 text-zen-ink"
                                >
                                    <span className="mt-2 h-1.5 w-1.5 bg-zen-olive rounded-full shrink-0" />
                                    <span className="text-sm leading-relaxed">
                                        {h}
                                    </span>
                                </li>
                            ))}
                        </ul>

                        <MapWidget
                            locations={[loc]}
                            center={[loc.lat, loc.lng]}
                            zoom={13}
                            height={420}
                        />
                    </div>

                    <aside className="lg:col-span-5 bg-zen-surface border border-zen-ink/10 p-8 lg:p-10 h-fit">
                        <div className="flex items-center gap-2 mb-6">
                            <Star size={14} fill="#C9A567" className="text-zen-gold" />
                            <span
                                className="text-[11px] uppercase tracking-[0.22em] text-zen-ink-soft"
                                data-testid="location-detail-rating"
                            >
                                {Number(rating).toFixed(1)} ·{" "}
                                {Number(reviewCount).toLocaleString()}+ Google reviews
                            </span>
                        </div>
                        <p className="font-serif text-2xl text-zen-ink font-light leading-tight mb-8">
                            {loc.name}
                        </p>
                        <div className="space-y-5 text-sm text-zen-ink">
                            <div className="flex items-start gap-3">
                                <MapPin size={16} className="text-zen-olive mt-1" />
                                <span>
                                    {loc.street}
                                    <br />
                                    {loc.city}, {loc.state} {loc.zip}
                                </span>
                            </div>
                            <div className="flex items-center gap-3">
                                <Phone size={16} className="text-zen-olive" />
                                <a
                                    href={`tel:${loc.phone_tel || loc.phone}`}
                                    className="hover:text-zen-olive"
                                >
                                    {loc.phone}
                                </a>
                            </div>
                            <div className="flex items-start gap-3">
                                <BadgeCheck size={16} className="text-zen-olive mt-1" />
                                <span className="font-mono text-xs text-zen-ink-soft">
                                    NY OCM License
                                    <br />
                                    {loc.license}
                                </span>
                            </div>
                            <div className="flex items-center gap-3">
                                <Truck size={16} className="text-zen-olive" />
                                <a
                                    href={loc.delivery_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:text-zen-olive"
                                >
                                    Same-day delivery zones →
                                </a>
                            </div>
                        </div>

                        <div className="mt-10 border-t border-zen-ink/10 pt-8">
                            <p className="text-[11px] uppercase tracking-[0.22em] text-zen-olive mb-4 inline-flex items-center gap-2">
                                <Clock size={11} /> Hours
                            </p>
                            <ul className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                                {loc.hours.map((h) => (
                                    <li key={h.day} className="contents">
                                        <div className="text-zen-ink-soft uppercase tracking-wider text-[11px]">
                                            {h.day}
                                        </div>
                                        <div className="text-zen-ink text-right text-[12px]">
                                            {h.open} – {h.close}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Explore this store: brings Brands + Contact + Menu
                            into the internal link graph directly from every
                            LocationDetail page. */}
                        <div className="mt-8 border-t border-zen-ink/10 pt-6 flex flex-wrap gap-x-4 gap-y-2 text-[12px] uppercase tracking-[0.22em]">
                            <Link
                                to={`/menu/${loc.slug}`}
                                className="text-zen-olive hover:text-zen-ink"
                                data-testid={`location-link-menu-${loc.slug}`}
                            >
                                Live menu
                            </Link>
                            <span className="text-zen-ink/20">·</span>
                            <Link
                                to={`/brands/${loc.slug}`}
                                className="text-zen-olive hover:text-zen-ink"
                                data-testid={`location-link-brands-${loc.slug}`}
                            >
                                Brands
                            </Link>
                            <span className="text-zen-ink/20">·</span>
                            <Link
                                to={`/${loc.slug === "queens-long-island" ? "queens" : loc.slug}/contact`}
                                className="text-zen-olive hover:text-zen-ink"
                                data-testid={`location-link-contact-${loc.slug}`}
                            >
                                Contact
                            </Link>
                        </div>
                    </aside>
                </div>
            </section>

            {/* DEALS (dark) */}
            <section className="relative py-12 sm:py-16 px-6 sm:px-10 bg-zen-elevated">
                <div className="zen-radial-olive absolute inset-0 pointer-events-none opacity-40" />
                <div className="relative max-w-[1400px] mx-auto">
                    <p className="text-[11px] uppercase tracking-[0.35em] text-zen-olive-light mb-5">
                        Weekly drops
                    </p>
                    <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-zen-cream leading-[1.05] mb-8 max-w-2xl">
                        Quietly priced deals.
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {loc.deals.map((d, i) => (
                            <div
                                key={i}
                                className="group relative border border-zen-cream/10 hover:border-zen-olive-light/60 bg-zen-elevated p-7 transition-all duration-500"
                            >
                                <div className="text-[10px] uppercase tracking-[0.3em] text-zen-olive-light mb-6">
                                    {d.day}
                                </div>
                                <h3 className="font-serif text-2xl text-zen-cream font-light leading-tight mb-3">
                                    {d.title}
                                </h3>
                                <p className="text-sm text-zen-cream-muted leading-relaxed mb-8">
                                    {d.body}
                                </p>
                                <a
                                    href={loc.menu_url || loc.store_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[11px] uppercase tracking-[0.3em] text-zen-cream border-b border-zen-cream/30 group-hover:border-zen-olive-light pb-1.5 transition-colors"
                                >
                                    Shop the deal
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SERVICE AREA */}
            <section className="bg-zen-surface border-y border-zen-ink/8">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-10 sm:py-14">
                    <p className="text-[11px] uppercase tracking-[0.35em] text-zen-olive mb-3 flex items-center gap-2">
                        <MapPin size={12} /> Service area ·{" "}
                        {loc.service_areas.length} neighborhoods
                    </p>
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-zen-ink leading-[1.1] mb-6 max-w-3xl">
                        Closest legal dispensary for these neighborhoods.
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                        {loc.service_areas.map((a) => (
                            <Link
                                key={a.city + a.zip}
                                to={`/dispensary-near/${slugify(a.city)}`}
                                data-testid={`service-area-${slugify(a.city)}`}
                                className="group bg-zen-bg border border-zen-ink/10 hover:border-zen-olive hover:bg-zen-bg/80 px-4 py-3 transition-all duration-200 flex flex-col justify-center"
                            >
                                <div className="text-[9px] uppercase tracking-[0.22em] text-zen-olive mb-1 truncate">
                                    {a.zip} · {a.minutes} min
                                </div>
                                <div className="font-serif text-base text-zen-ink font-light flex items-center justify-between leading-tight gap-2">
                                    <span className="truncate">{a.city}</span>
                                    <ArrowUpRight
                                        size={14}
                                        className="opacity-30 group-hover:opacity-100 flex-shrink-0 transition-opacity"
                                    />
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}
